import type {
  AdminUserBody,
  AdminUserDto,
  AiBatchDto,
  AiDraftDto,
  ApprovalBody,
  ApprovalDto,
  ChannelDto,
  CreateTaskInput,
  DepartmentDto,
  DraftPatchBody,
  HealthDto,
  IdeaDto,
  IdeaStatus,
  InviteDto,
  MeDto,
  NoteColor,
  NoteDto,
  Paged,
  PhotoDto,
  ProjectDto,
  RecurringPreviewDto,
  RecurringRuleDto,
  SettingsDto,
  SettingsPatch,
  ShiftStatsDto,
  TaskCommand,
  TaskDetailDto,
  TaskListItemDto,
  TaskListQuery,
  TaskPatchBody,
  TaskStatus,
  TeamMemberDto,
  TimelineItemDto,
  UserBrief,
  WeeklyReportDto,
} from '@ovo/shared'
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { setUserTimezone } from '../lib/dates.ts'
import { ACTION_SUCCESS, isOpen, predictStatus } from '../lib/status.ts'
import { haptic } from '../telegram/haptics.ts'
import { snackbar } from '../ui/Snackbar.tsx'
import type { Query } from './client.ts'
import { api, errorMessage, toList } from './client.ts'
import type {
  BatchCommitInput,
  CommentInput,
  CommitResult,
  DepartmentInput,
  InviteInput,
  PendingRequestItem,
  RecurringInput,
} from './types.ts'

// Ключи кэша React Query

export const qk = {
  me: ['me'] as const,
  tasksAll: ['tasks'] as const,
  tasks: (q: TaskListQuery) => ['tasks', q] as const,
  task: (id: number) => ['task', id] as const,
  users: (q: string) => ['users', q] as const,
  team: ['team'] as const,
  report: ['reports', 'weekly'] as const,
  recurring: ['recurring'] as const,
  batch: (id: number) => ['ai-batch', id] as const,
  adminUsers: ['admin', 'users'] as const,
  adminDepartments: ['admin', 'departments'] as const,
  health: ['admin', 'health'] as const,
  projects: ['projects'] as const,
  channels: ['channels'] as const,
  ideas: (scope: 'mine' | 'all') => ['ideas', scope] as const,
  approvals: (scope: 'mine' | 'to_me') => ['approvals', scope] as const,
  shiftsMe: ['shifts', 'me'] as const,
  shiftsTeam: ['shifts', 'team'] as const,
  flags: (userId: number) => ['flags', userId] as const,
  notes: ['notes'] as const,
  photo: (userId: number, version: number) => ['photo', userId, version] as const,
}

function isTaskDetail(v: unknown): v is TaskDetailDto {
  return typeof v === 'object' && v !== null && 'id' in v && 'allowedActions' in v
}

// ───────── Профиль ─────────

export function useMe() {
  return useQuery({
    queryKey: qk.me,
    queryFn: async () => {
      const me = await api.get<MeDto>('/me')
      setUserTimezone(me.settings.timezone)
      return me
    },
    staleTime: 5 * 60_000,
  })
}

export function useUpdateSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: SettingsPatch) => api.patch<SettingsDto | MeDto>('/me/settings', patch),
    onSuccess: (data, patch) => {
      qc.setQueryData<MeDto>(qk.me, (old) => {
        if (!old) return old
        if (data && 'settings' in data) return data
        const settings = data && 'timezone' in data ? data : { ...old.settings, ...patch }
        return { ...old, settings: settings as SettingsDto }
      })
      const me = qc.getQueryData<MeDto>(qk.me)
      if (me) setUserTimezone(me.settings.timezone)
      haptic.notify('success')
      snackbar.success('Настройки сохранены')
    },
    onError: (e) => {
      haptic.notify('error')
      snackbar.error(errorMessage(e))
    },
  })
}

// ───────── Задачи ─────────

export function useTasks(query: TaskListQuery, enabled = true) {
  return useQuery({
    queryKey: qk.tasks(query),
    queryFn: ({ signal }) =>
      api.get<Paged<TaskListItemDto>>('/tasks', { limit: 100, ...query } as Query, signal),
    select: toList,
    enabled,
  })
}

export function useTask(id: number | null) {
  return useQuery({
    queryKey: qk.task(id ?? 0),
    queryFn: ({ signal }) => api.get<TaskDetailDto>(`/tasks/${id}`, undefined, signal),
    enabled: id !== null && id > 0,
  })
}

type TaskLike = Pick<TaskListItemDto, 'status' | 'requiresAcceptance' | 'creator' | 'assignee'>

export interface TaskActionVars {
  taskId: number
  command: TaskCommand
  /** Для предсказания нового статуса; если не передать — ищем в кэше. */
  task?: TaskLike
  /** Не показывать снекбар (например, действие внутри мастера). */
  silent?: boolean
}

interface ActionContext {
  lists: Array<[QueryKey, Paged<TaskListItemDto> | undefined]>
  detail: TaskDetailDto | undefined
}

function findTask(qc: QueryClient, id: number): TaskLike | undefined {
  const detail = qc.getQueryData<TaskDetailDto>(qk.task(id))
  if (detail) return detail
  for (const [, data] of qc.getQueriesData<Paged<TaskListItemDto>>({ queryKey: qk.tasksAll })) {
    const found = data?.items.find((t) => t.id === id)
    if (found) return found
  }
  return undefined
}

function patchItem<T extends TaskListItemDto>(item: T, command: TaskCommand, status: TaskStatus | null): T {
  const next: T = { ...item }
  if (status) next.status = status
  switch (command.type) {
    case 'set_due':
      next.dueAt = command.dueAt
      next.isOverdue = command.dueAt ? new Date(command.dueAt).getTime() < Date.now() : false
      break
    case 'request_due':
      next.pendingRequest = 'due_change'
      break
    case 'ask':
      next.pendingRequest = 'question'
      break
    case 'answer':
    case 'decide_due':
      next.pendingRequest = null
      if (command.type === 'decide_due' && command.approve) next.isOverdue = false
      break
    default:
      break
  }
  if (status && !isOpen(status)) next.isOverdue = false
  if (status === 'submitted') next.isOverdue = false
  return next
}

/** POST /tasks/:id/actions с оптимистичным статусом, откатом и снекбаром. */
export function useTaskAction() {
  const qc = useQueryClient()
  return useMutation<TaskDetailDto | null, Error, TaskActionVars, ActionContext>({
    mutationFn: ({ taskId, command }) => api.post<TaskDetailDto | null>(`/tasks/${taskId}/actions`, command),
    onMutate: async ({ taskId, command, task }) => {
      await Promise.all([
        qc.cancelQueries({ queryKey: qk.tasksAll }),
        qc.cancelQueries({ queryKey: qk.task(taskId) }),
      ])
      const lists = qc.getQueriesData<Paged<TaskListItemDto>>({ queryKey: qk.tasksAll })
      const detail = qc.getQueryData<TaskDetailDto>(qk.task(taskId))
      const base = task ?? findTask(qc, taskId)
      const status = base ? predictStatus({ ...base, prevStatus: null }, command) : null

      for (const [key, data] of lists) {
        if (!data) continue
        const query = (key[1] ?? {}) as TaskListQuery
        const openOnly = (query.state ?? 'open') === 'open'
        let items = data.items.map((t) => (t.id === taskId ? patchItem(t, command, status) : t))
        if (openOnly && status && !isOpen(status)) items = items.filter((t) => t.id !== taskId)
        qc.setQueryData<Paged<TaskListItemDto>>(key, { ...data, items })
      }
      if (detail) qc.setQueryData<TaskDetailDto>(qk.task(taskId), patchItem(detail, command, status))
      haptic.impact('light')
      return { lists, detail }
    },
    onError: (e, { taskId }, ctx) => {
      if (ctx) {
        for (const [key, data] of ctx.lists) qc.setQueryData(key, data)
        if (ctx.detail) qc.setQueryData(qk.task(taskId), ctx.detail)
      }
      haptic.notify('error')
      snackbar.error(errorMessage(e))
    },
    onSuccess: (data, { taskId, command, silent }) => {
      if (isTaskDetail(data)) qc.setQueryData(qk.task(taskId), data)
      haptic.notify('success')
      const text = ACTION_SUCCESS[command.type]
      if (!silent && text) snackbar.success(text)
    },
    onSettled: (_d, _e, { taskId }) => {
      void qc.invalidateQueries({ queryKey: qk.tasksAll })
      void qc.invalidateQueries({ queryKey: qk.task(taskId) })
      void qc.invalidateQueries({ queryKey: qk.team })
    },
  })
}

export function useCreateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateTaskInput) => api.post<TaskDetailDto>('/tasks', input),
    onSuccess: (task) => {
      if (isTaskDetail(task)) qc.setQueryData(qk.task(task.id), task)
      void qc.invalidateQueries({ queryKey: qk.tasksAll })
      void qc.invalidateQueries({ queryKey: qk.team })
      haptic.notify('success')
    },
    onError: (e) => {
      haptic.notify('error')
      snackbar.error(errorMessage(e))
    },
  })
}

export function usePatchTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: TaskPatchBody }) =>
      api.patch<TaskDetailDto>(`/tasks/${id}`, patch),
    onSuccess: (task, { id }) => {
      if (isTaskDetail(task)) qc.setQueryData(qk.task(id), task)
      void qc.invalidateQueries({ queryKey: qk.task(id) })
      void qc.invalidateQueries({ queryKey: qk.tasksAll })
      haptic.notify('success')
    },
    onError: (e) => {
      haptic.notify('error')
      snackbar.error(errorMessage(e))
    },
  })
}

// ───────── Люди и процессы ─────────

export function useIdeas(scope: 'mine' | 'all', enabled = true) {
  return useQuery({
    queryKey: qk.ideas(scope),
    queryFn: ({ signal }) => api.get<IdeaDto[]>('/ideas', { scope }, signal),
    enabled,
  })
}

export function useCreateIdea() {
  const qc = useQueryClient()
  return useMutation<IdeaDto, Error, { text: string; anonymous?: boolean }>({
    mutationFn: (body) => api.post<IdeaDto>('/ideas', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ideas'] }),
  })
}

export function useIdeaStatus() {
  const qc = useQueryClient()
  return useMutation<
    IdeaDto,
    Error,
    { id: number; status: IdeaStatus; plannedAt?: string | null; note?: string | null }
  >({
    mutationFn: ({ id, ...body }) => api.patch<IdeaDto>(`/ideas/${id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ideas'] }),
  })
}

export function useApprovals(scope: 'mine' | 'to_me', enabled = true) {
  return useQuery({
    queryKey: qk.approvals(scope),
    queryFn: ({ signal }) => api.get<ApprovalDto[]>('/approvals', { scope }, signal),
    enabled,
  })
}

export function useCreateApproval() {
  const qc = useQueryClient()
  return useMutation<ApprovalDto, Error, ApprovalBody>({
    mutationFn: (body) => api.post<ApprovalDto>('/approvals', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['approvals'] }),
  })
}

export function useDecideApproval() {
  const qc = useQueryClient()
  return useMutation<ApprovalDto, Error, { id: number; approve: boolean; note?: string | null }>({
    mutationFn: ({ id, ...body }) => api.post<ApprovalDto>(`/approvals/${id}/decision`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['approvals'] }),
  })
}

export function useMyShifts() {
  return useQuery({
    queryKey: qk.shiftsMe,
    queryFn: ({ signal }) => api.get<ShiftStatsDto>('/shifts/me', { days: 30 }, signal),
  })
}

/** Начать или завершить рабочий день с сайта (как кнопки в боте). */
export function useShiftToggle() {
  const qc = useQueryClient()
  return useMutation<unknown, Error, 'start' | 'end'>({
    mutationFn: (op) => api.post(`/shifts/${op}`),
    onSuccess: (_d, op) => {
      haptic.notify('success')
      snackbar.success(
        op === 'start' ? 'Рабочий день начат — хорошего дня!' : 'Рабочий день завершён. Спасибо!',
      )
    },
    onError: (e) => {
      haptic.notify('error')
      snackbar.error(errorMessage(e))
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['shifts'] }),
  })
}

// ───────── Фото профиля ─────────

/** Фото как data URL; версия в ключе — новое фото загрузится, старое останется в кэше до смены. */
export function usePhoto(userId: number, version: number | null | undefined) {
  return useQuery({
    queryKey: qk.photo(userId, version ?? 0),
    queryFn: async ({ signal }) => {
      const p = await api.get<PhotoDto>(`/users/${userId}/photo`, undefined, signal)
      return `data:${p.mime};base64,${p.data}`
    },
    enabled: Boolean(version) && userId > 0,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 60 * 60_000,
    retry: false,
  })
}

export function useUploadPhoto() {
  const qc = useQueryClient()
  return useMutation<{ photoVersion: number }, Error, { mime: PhotoDto['mime']; data: string }>({
    mutationFn: (body) => api.put('/me/photo', body),
    onSuccess: ({ photoVersion }, body) => {
      const me = qc.getQueryData<MeDto>(qk.me)
      if (me) {
        qc.setQueryData(qk.photo(me.id, photoVersion), `data:${body.mime};base64,${body.data}`)
        qc.setQueryData<MeDto>(qk.me, { ...me, photoVersion })
      }
      haptic.notify('success')
      snackbar.success('Фото обновлено')
    },
    onError: (e) => {
      haptic.notify('error')
      snackbar.error(errorMessage(e))
    },
  })
}

export function useDeletePhoto() {
  const qc = useQueryClient()
  return useMutation<unknown, Error, void>({
    mutationFn: () => api.del('/me/photo'),
    onSuccess: () => {
      qc.setQueryData<MeDto>(qk.me, (me) => (me ? { ...me, photoVersion: null } : me))
      snackbar.success('Фото убрано')
    },
    onError: (e) => snackbar.error(errorMessage(e)),
  })
}

// ───────── Заметки-стикеры ─────────

export function useNotes() {
  return useQuery({
    queryKey: qk.notes,
    queryFn: ({ signal }) => api.get<NoteDto[]>('/notes', undefined, signal),
  })
}

export function useCreateNote() {
  const qc = useQueryClient()
  return useMutation<NoteDto, Error, { text: string; color?: NoteColor }>({
    mutationFn: (body) => api.post<NoteDto>('/notes', body),
    onSuccess: (note) => qc.setQueryData<NoteDto[]>(qk.notes, (old) => [note, ...(old ?? [])]),
    onError: (e) => snackbar.error(errorMessage(e)),
  })
}

export function useUpdateNote() {
  const qc = useQueryClient()
  return useMutation<
    NoteDto,
    Error,
    { id: number; text?: string; color?: NoteColor; taskId?: number | null },
    { prev: NoteDto[] | undefined }
  >({
    mutationFn: ({ id, ...patch }) => api.patch<NoteDto>(`/notes/${id}`, patch),
    // Цвет и текст меняем сразу, не дожидаясь сервера
    onMutate: async ({ id, text, color }) => {
      await qc.cancelQueries({ queryKey: qk.notes })
      const prev = qc.getQueryData<NoteDto[]>(qk.notes)
      qc.setQueryData<NoteDto[]>(qk.notes, (old) =>
        old?.map((n) =>
          n.id === id ? { ...n, ...(text !== undefined ? { text } : {}), ...(color ? { color } : {}) } : n,
        ),
      )
      return { prev }
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.notes, ctx.prev)
      snackbar.error(errorMessage(e))
    },
    onSuccess: (note) =>
      qc.setQueryData<NoteDto[]>(qk.notes, (old) => old?.map((n) => (n.id === note.id ? note : n))),
  })
}

export function useDeleteNote() {
  const qc = useQueryClient()
  return useMutation<unknown, Error, number, { prev: NoteDto[] | undefined }>({
    mutationFn: (id) => api.del(`/notes/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: qk.notes })
      const prev = qc.getQueryData<NoteDto[]>(qk.notes)
      qc.setQueryData<NoteDto[]>(qk.notes, (old) => old?.filter((n) => n.id !== id))
      return { prev }
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.notes, ctx.prev)
      snackbar.error(errorMessage(e))
    },
  })
}

/** Новый порядок стикеров (перетаскивание). */
export function useReorderNotes() {
  const qc = useQueryClient()
  return useMutation<unknown, Error, number[], { prev: NoteDto[] | undefined }>({
    mutationFn: (ids) => api.post('/notes/order', { ids }),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: qk.notes })
      const prev = qc.getQueryData<NoteDto[]>(qk.notes)
      if (prev) {
        const byId = new Map(prev.map((n) => [n.id, n]))
        qc.setQueryData<NoteDto[]>(
          qk.notes,
          ids.flatMap((id) => byId.get(id) ?? []),
        )
      }
      return { prev }
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.notes, ctx.prev)
      snackbar.error(errorMessage(e))
    },
  })
}

export function useTeamShifts(enabled = true) {
  return useQuery({
    queryKey: qk.shiftsTeam,
    queryFn: ({ signal }) => api.get<ShiftStatsDto[]>('/shifts/team', { days: 30 }, signal),
    enabled,
  })
}

export function usePeopleFlags(userId: number | null) {
  return useQuery({
    queryKey: qk.flags(userId ?? 0),
    queryFn: ({ signal }) =>
      api.get<{ shiftsEnabled: boolean; standupEnabled: boolean }>(
        `/team/${userId}/flags`,
        undefined,
        signal,
      ),
    enabled: userId !== null,
    retry: false,
  })
}

export function useSetPeopleFlags(userId: number) {
  const qc = useQueryClient()
  return useMutation<
    { shiftsEnabled: boolean; standupEnabled: boolean },
    Error,
    { shiftsEnabled?: boolean; standupEnabled?: boolean }
  >({
    mutationFn: (body) => api.patch(`/team/${userId}/flags`, body),
    onSuccess: (data) => qc.setQueryData(qk.flags(userId), data),
  })
}

/** Проекты и клиенты (метки задач). */
/** Каналы, где бот видит посты (для проверки публикаций). */
export function useChannels() {
  return useQuery({
    queryKey: qk.channels,
    queryFn: ({ signal }) => api.get<ChannelDto[]>('/channels', undefined, signal),
    staleTime: 5 * 60_000,
  })
}

export function useProjects() {
  return useQuery({
    queryKey: qk.projects,
    queryFn: ({ signal }) => api.get<ProjectDto[]>('/projects', undefined, signal),
    staleTime: 5 * 60_000,
  })
}

export function useCreateProject() {
  const qc = useQueryClient()
  return useMutation<ProjectDto, Error, { name: string; kind?: 'project' | 'client' }>({
    mutationFn: (body) => api.post<ProjectDto>('/projects', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.projects }),
  })
}

/** «✨ Предложить» для «Готово, когда…»: POST /ai/suggest-dod. */
export function useSuggestDod() {
  return useMutation<{ dod: string }, Error, { title: string; description?: string | null }>({
    mutationFn: (body) => api.post<{ dod: string }>('/ai/suggest-dod', body),
  })
}

/** Комментарий: POST /tasks/:id/comments, сразу показываем в ленте. */
export function useAddComment(taskId: number) {
  const qc = useQueryClient()
  return useMutation<unknown, Error, CommentInput, { detail: TaskDetailDto | undefined }>({
    mutationFn: (body) => api.post(`/tasks/${taskId}/comments`, body),
    onMutate: async ({ text }) => {
      await qc.cancelQueries({ queryKey: qk.task(taskId) })
      const detail = qc.getQueryData<TaskDetailDto>(qk.task(taskId))
      const me = qc.getQueryData<MeDto>(qk.me)
      if (detail) {
        const item: TimelineItemDto = {
          kind: 'comment',
          id: `tmp-${Date.now()}`,
          at: new Date().toISOString(),
          actor: me ? { id: me.id, displayName: me.displayName } : null,
          commentKind: 'comment',
          body: text,
        }
        qc.setQueryData<TaskDetailDto>(qk.task(taskId), { ...detail, timeline: [...detail.timeline, item] })
      }
      return { detail }
    },
    onError: (e, _v, ctx) => {
      if (ctx?.detail) qc.setQueryData(qk.task(taskId), ctx.detail)
      haptic.notify('error')
      snackbar.error(errorMessage(e))
    },
    onSuccess: () => haptic.notify('success'),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.task(taskId) }),
  })
}

// ───────── Люди и команда ─────────

export function useUsers(q: string, enabled = true) {
  return useQuery({
    queryKey: qk.users(q),
    queryFn: ({ signal }) => api.get<UserBrief[] | Paged<UserBrief>>('/users', { q, limit: 100 }, signal),
    select: toList,
    enabled,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  })
}

/** GET /tasks/similar — похожие открытые задачи у исполнителей (предупреждение о дубле). */
export function useSimilarTasks(title: string, assigneeIds: number[]) {
  const t = title.trim()
  return useQuery({
    queryKey: ['similarTasks', t, assigneeIds.join(',')],
    queryFn: ({ signal }) =>
      api.get<{ id: number; title: string }[]>(
        '/tasks/similar',
        { title: t, assigneeIds: assigneeIds.join(',') },
        signal,
      ),
    enabled: t.length >= 5 && assigneeIds.length > 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

/** GET /users/:id/load — открытые и срочные задачи сотрудника (предупреждение при постановке). */
export function useUserLoad(userId: number | null) {
  return useQuery({
    queryKey: ['userLoad', userId],
    queryFn: ({ signal }) =>
      api.get<{ open: number; urgent: number; limits: { open: number; urgent: number } }>(
        `/users/${userId}/load`,
        undefined,
        signal,
      ),
    enabled: userId !== null,
    staleTime: 60_000,
  })
}

/**
 * GET /tasks/estimate — сколько обычно занимают похожие задачи у исполнителя (по истории закрытых)
 * и не тесноват ли срок. text = null — подсказки нет (мало истории или темп человека вам не виден).
 */
export function useDurationHint(
  title: string,
  assigneeId: number | null,
  dueAt: string | null,
  coAssigneeIds: number[],
) {
  const t = title.trim()
  const co = coAssigneeIds.join(',')
  return useQuery({
    queryKey: ['durationHint', t, assigneeId, dueAt, co],
    queryFn: ({ signal }) =>
      api.get<{ text: string | null; tight: boolean }>(
        '/tasks/estimate',
        { title: t, assigneeId, dueAt, coAssigneeIds: co },
        signal,
      ),
    enabled: t.length >= 5 && assigneeId !== null,
    staleTime: 60_000,
    // Пока печатают название — держим прошлую подсказку; сменили исполнителя — чужую не показываем
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === assigneeId ? prev : undefined),
  })
}

/** GET /team: прямые подчинённые; branch — вся ветка (?branch=1). */
export function useTeam(branch: boolean, enabled = true) {
  return useQuery({
    queryKey: [...qk.team, branch ? 'branch' : 'direct'],
    queryFn: ({ signal }) =>
      api.get<TeamMemberDto[] | Paged<TeamMemberDto>>('/team', branch ? { branch: '1' } : undefined, signal),
    select: toList,
    enabled,
  })
}

/** GET /requests/pending: вопросы и переносы по задачам, где я постановщик. */
export function usePendingRequests(enabled = true) {
  return useQuery({
    queryKey: ['requests', 'pending'],
    queryFn: ({ signal }) => api.get<PendingRequestItem[]>('/requests/pending', undefined, signal),
    enabled,
  })
}

export function useWeeklyReport() {
  return useQuery({
    queryKey: qk.report,
    queryFn: ({ signal }) => api.get<WeeklyReportDto>('/reports/weekly', undefined, signal),
  })
}

// ───────── Регулярные задачи ─────────

export function useRecurring() {
  return useQuery({
    queryKey: qk.recurring,
    queryFn: ({ signal }) =>
      api.get<RecurringRuleDto[] | Paged<RecurringRuleDto>>('/recurring', undefined, signal),
    select: toList,
  })
}

/** По каким дням будет ставиться задача — считает сервер (производственный календарь). */
export function useRecurringPreview(
  input: { assigneeId?: number; schedule: Record<string, unknown> } | null,
) {
  return useQuery({
    queryKey: ['recurring-preview', input],
    queryFn: () => api.post<RecurringPreviewDto>('/recurring/preview', input),
    enabled: input !== null,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: false,
  })
}

export function useSaveRecurring() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: number | null; input: Partial<RecurringInput> }) =>
      id
        ? api.patch<RecurringRuleDto>(`/recurring/${id}`, input)
        : api.post<RecurringRuleDto>('/recurring', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.recurring })
      haptic.notify('success')
    },
    onError: (e) => {
      haptic.notify('error')
      snackbar.error(errorMessage(e))
    },
  })
}

export function useDeleteRecurring() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.del(`/recurring/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.recurring })
      haptic.notify('success')
      snackbar.success('Правило удалено')
    },
    onError: (e) => snackbar.error(errorMessage(e)),
  })
}

// ───────── ИИ-черновики ─────────

export function useBatch(id: number | null) {
  return useQuery({
    queryKey: qk.batch(id ?? 0),
    queryFn: ({ signal }) => api.get<AiBatchDto>(`/ai/batches/${id}`, undefined, signal),
    enabled: id !== null && id > 0,
    // пока ИИ разбирает сообщение — опрашиваем
    refetchInterval: (q) => (q.state.data?.status === 'processing' ? 1500 : false),
  })
}

export function usePatchDraft(batchId: number) {
  const qc = useQueryClient()
  return useMutation<
    AiDraftDto | null,
    Error,
    { id: number; patch: DraftPatchBody; optimistic?: Partial<AiDraftDto> },
    { batch: AiBatchDto | undefined }
  >({
    mutationFn: ({ id, patch }) => api.patch<AiDraftDto | null>(`/ai/drafts/${id}`, patch),
    onMutate: async ({ id, optimistic }) => {
      await qc.cancelQueries({ queryKey: qk.batch(batchId) })
      const batch = qc.getQueryData<AiBatchDto>(qk.batch(batchId))
      if (batch && optimistic) {
        qc.setQueryData<AiBatchDto>(qk.batch(batchId), {
          ...batch,
          drafts: batch.drafts.map((d) => (d.id === id ? { ...d, ...optimistic } : d)),
        })
      }
      return { batch }
    },
    onSuccess: (draft) => {
      if (draft && typeof draft === 'object' && 'id' in draft) {
        qc.setQueryData<AiBatchDto>(qk.batch(batchId), (b) =>
          b ? { ...b, drafts: b.drafts.map((d) => (d.id === draft.id ? draft : d)) } : b,
        )
      }
    },
    onError: (e, _v, ctx) => {
      if (ctx?.batch) qc.setQueryData(qk.batch(batchId), ctx.batch)
      snackbar.error(errorMessage(e))
    },
  })
}

export function useCommitBatch(batchId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: BatchCommitInput) => api.post<CommitResult>(`/ai/batches/${batchId}/commit`, body),
    onSuccess: (data) => {
      // сервер возвращает свежий пакет в batch
      if (data?.batch) qc.setQueryData(qk.batch(batchId), data.batch)
      void qc.invalidateQueries({ queryKey: qk.batch(batchId) })
      void qc.invalidateQueries({ queryKey: qk.tasksAll })
      haptic.notify('success')
    },
    onError: (e) => {
      haptic.notify('error')
      snackbar.error(errorMessage(e))
    },
  })
}

// ───────── Админка ─────────

export function useAdminUsers(enabled = true) {
  return useQuery({
    queryKey: qk.adminUsers,
    queryFn: ({ signal }) => api.get<AdminUserDto[] | Paged<AdminUserDto>>('/admin/users', undefined, signal),
    select: toList,
    enabled,
  })
}

export function useSaveAdminUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number | null; body: AdminUserBody }) =>
      id ? api.patch<AdminUserDto>(`/admin/users/${id}`, body) : api.post<AdminUserDto>('/admin/users', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.adminUsers })
      void qc.invalidateQueries({ queryKey: ['users'] })
      haptic.notify('success')
    },
    onError: (e) => {
      haptic.notify('error')
      snackbar.error(errorMessage(e))
    },
  })
}

export function useAdminDepartments(enabled = true) {
  return useQuery({
    queryKey: qk.adminDepartments,
    queryFn: ({ signal }) =>
      api.get<DepartmentDto[] | Paged<DepartmentDto>>('/admin/departments', undefined, signal),
    select: toList,
    enabled,
  })
}

export function useSaveDepartment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number | null; body: DepartmentInput }) =>
      id
        ? api.patch<DepartmentDto>(`/admin/departments/${id}`, body)
        : api.post<DepartmentDto>('/admin/departments', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.adminDepartments })
      haptic.notify('success')
      snackbar.success('Отдел сохранён')
    },
    onError: (e) => {
      haptic.notify('error')
      snackbar.error(errorMessage(e))
    },
  })
}

export function useCreateInvite() {
  const qc = useQueryClient()
  return useMutation({
    // основной путь сервера; POST /admin/invites { userId } — запасной синоним
    mutationFn: (body: InviteInput) => api.post<InviteDto>(`/admin/users/${body.userId}/invite`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.adminUsers })
      haptic.notify('success')
    },
    onError: (e) => {
      haptic.notify('error')
      snackbar.error(errorMessage(e))
    },
  })
}

export function useHealth(enabled = true) {
  return useQuery({
    queryKey: qk.health,
    queryFn: ({ signal }) => api.get<HealthDto>('/admin/health', undefined, signal),
    enabled,
    refetchInterval: 30_000,
  })
}
